import React, {ChangeEvent, useContext, useEffect} from 'react';
import Label from 'shared/components/label';
import BaseStep from 'shared/registration/components/steps/base-step';
import FormikTextField from 'src/components/FormikTextField';
import text from '../text.json';
import placeholders from 'src/static/placeholders.json';
import {OnboardingContext} from 'src/components/Onboarding/context';
import {useFormikContext} from 'formik';
import {MobileFormState, MobileNumberForm} from 'src/types/onboarding';
import {Box, Link} from '@mui/material';
import UserService from 'src/services/UserService';
import {FormikContextType} from 'formik/dist/types';
import FormikTextInput from 'src/components/inputs/formik-text-input';
import {ObjectOf} from 'src/types/store';

const stepText = text.phoneNumber;

function getStateDetails(form: FormikContextType<MobileNumberForm>): ObjectOf<string> {
  const mobile = form.values.mobile || '';
  const hasMobile = mobile.length > 0;

  switch (form.values.mobileFormState) {
    case MobileFormState.VERIFIED:
      return {
        primaryButton: 'Continue',
      };
    case MobileFormState.CODE_SENT:
      return {
        primaryButton: 'Verify',
      };
    case MobileFormState.INITIAL:
    default:
      return {
        primaryButton: hasMobile ? 'Verify' : 'Continue',
      };
  }
}

export default function MobileNumber(): React.ReactElement {
  const onboardingContext = useContext(OnboardingContext);
  const form = useFormikContext<MobileNumberForm>();
  const mobile = form.values.mobile || '';
  const hasMobile = mobile.length > 0;
  const entryState = form.values.mobileFormState;
  const codeSent = entryState === MobileFormState.CODE_SENT;
  const mobileVerified = entryState === MobileFormState.VERIFIED;

  useEffect(() => {
    if (hasMobile && form.values.mobileVerified) {
      form.setFieldValue('mobileFormState', MobileFormState.VERIFIED);
    } else {
      form.setFieldValue('mobileFormState', MobileFormState.INITIAL);
    }
  }, []);

  useEffect(() => {
    const details = getStateDetails(form);
    onboardingContext.updatePrimaryButton({
      text: details.primaryButton,
      controlled: true,
    });
  }, [mobile, entryState]);

  function skipStep(): void {
    form.resetForm();
    onboardingContext.skipStep();
  }

  async function resendAsCall(): Promise<void> {
    onboardingContext.setLoading(true);
    await UserService.sendPhoneVerification(mobile, 'call');
    form.setFieldValue('mobileFormState', MobileFormState.CODE_SENT);
    onboardingContext.setLoading(false);
  }

  function updateMobile(event: ChangeEvent<HTMLInputElement>): void {
    const updatedMobile = event.target.value;
    const isMobileVerified =
      form.initialValues.mobile === updatedMobile && form.initialValues.mobileVerified;
    form.setValues({
      ...form.values,
      mobileVerified: isMobileVerified,
      mobile: updatedMobile,
      mobileFormState: isMobileVerified ? MobileFormState.VERIFIED : MobileFormState.INITIAL,
    });
  }

  const SkipStep = (
    <Link
      sx={{verticalAlign: 'unset'}}
      component="button"
      onClick={skipStep}
      data-cy="skip"
      underline="hover"
    >
      {codeSent ? 'skip' : 'Skip'}
    </Link>
  );

  return (
    <BaseStep stepHeader={stepText.title} subHeader={stepText.subtitle} step>
      <Label>
        Mobile Number
        <FormikTextInput
          name="mobile"
          onChange={updateMobile}
          autoFocus
          placeholder={placeholders.tel}
          autoComplete="tel"
          aria-label="Mobile Number"
          aria-describedby="stepHeader"
          cy="mobile"
        />
      </Label>
      <Box textAlign="left" mt={2}>
        {codeSent ? (
          <div>
            <p className="shared-font body">
              A one-time code has been sent to {mobile}. Please enter your verification code to
              enable alerts.
            </p>
            <Label required>
              Code
              <FormikTextField
                name="verificationCode"
                required
                autoFocus
                type="text"
                placeholder={placeholders.verificationCode}
                aria-label="Mobile Verification Code"
                cy="verificationCode"
              />
            </Label>
            <Box mt={3}>
              <p className="shared-font body">
                Didn&apos;t receive a code? Double check the number you entered. You can also{' '}
                <Link
                  component="button"
                  sx={{verticalAlign: 'unset'}}
                  onClick={resendAsCall}
                  underline="hover"
                >
                  resend as a call
                </Link>{' '}
                or {SkipStep}.
              </p>
            </Box>
          </div>
        ) : (
          !mobileVerified && (
            <p className="shared-font body">Don&apos;t have a phone number? {SkipStep}</p>
          )
        )}
      </Box>
    </BaseStep>
  );
}
